import * as React from 'react';
import getPlaceholderAndAriaLabel from '../selectors/getPlaceholderAndAriaLabel';
import getScenarioStore from '../selectors/getScenarioStore';
import KeyboardCharCodes from 'owa-hotkeys/lib/utils/keyboardCharCodes';
import { computed } from 'mobx';
import { observer } from 'mobx-react';
import { SearchScenarioId } from '../store/schema/SearchScenarioId';
import { SUGGESTION_ID_PREFIX, SUGGESTIONS_CALLOUT_ID } from '../searchConstants';
import {
    onSearchTextChanged,
    setIsSuggestionsDropdownVisible,
    getSuggestions,
    setSearchTextForSuggestion,
} from '../actions/publicActions';
import {
    onEscapePressedSearchInput,
    onKeyDownSearchInput,
    onSearchInputFocused,
    onEnterPressedSearchInput,
} from '../actions/internalActions';
import {
    lazyOnBackspacePressedSearchInput,
    lazyOnDownArrowPressedSearchInput,
    lazyOnLeftArrowPressedSearchInput,
    lazyOnUpArrowPressedSearchInput,
} from 'owa-search';

const classNames = require('classnames');
const styles = require('./SearchInput.scss');

export interface SearchInputProps {
    searchPlaceHolderText?: string;
    isDisabled?: boolean; // Boolean indicating if search box is disabled (default is false)
    scenarioId: SearchScenarioId;
    isSuggestionsCalloutVisible: boolean;
}

@observer
export default class SearchInput extends React.Component<SearchInputProps, {}> {
    private searchInput: HTMLInputElement;

    /**
     * Sets focus on the search input. This is exposed publicly so parent component
     * (SearchBox) can set focus on the input element as a result of clicks on other
     * parts of the search box.
     */
    public setFocus() {
        if (this.searchInput) {
            this.searchInput.focus();
        }
    }

    /**
     * This computed returns the text to be displayed in the actual input
     * element.
     */
    @computed
    get searchInputText(): string {
        return getScenarioStore(this.props.scenarioId).searchText;
    }

    @computed
    get activeDescendant(): string {
        const { isSuggestionsCalloutVisible, scenarioId } = this.props;
        const { selectedSuggestionIndex } = getScenarioStore(scenarioId);

        /**
         * If search suggestions are visible and the user has a suggestion
         * selected, then return a string to be used for the component's
         * aria-activedescendant attribute.
         */
        if (isSuggestionsCalloutVisible && selectedSuggestionIndex >= 0) {
            return `${SUGGESTION_ID_PREFIX}${selectedSuggestionIndex}`;
        }

        return '';
    }

    render() {
        const {
            scenarioId,
            searchPlaceHolderText,
            isDisabled,
            isSuggestionsCalloutVisible,
        } = this.props;

        const { searchSessionGuid } = getScenarioStore(this.props.scenarioId);
        const isSearchBoxExpanded = !!searchSessionGuid;

        const searchInputClasses = classNames(
            styles.searchBoxInput,
            isDisabled && styles.isDisabled,
            isSearchBoxExpanded ? styles.inSearchinputPlaceholder : styles.inputPlaceholder
        );

        const { ariaLabel, placeholder } = getPlaceholderAndAriaLabel(
            scenarioId,
            searchPlaceHolderText
        );

        return (
            <div className={styles.searchBoxInputContainer}>
                <input
                    aria-activedescendant={this.activeDescendant}
                    aria-autocomplete="list"
                    aria-label={ariaLabel}
                    aria-owns={isSuggestionsCalloutVisible ? SUGGESTIONS_CALLOUT_ID : ''}
                    autoComplete="off"
                    className={searchInputClasses}
                    disabled={isDisabled}
                    onChange={this.onSearchTextChange}
                    onClick={this.onClick}
                    onFocus={this.onFocus}
                    onKeyDown={this.onKeyDown}
                    placeholder={placeholder}
                    ref={ref => (this.searchInput = ref)}
                    value={this.searchInputText}
                    spellCheck={false}
                    autoCorrect="off"
                />
            </div>
        );
    }

    /**
     * Key handler for special key input into the input element.
     */
    private onKeyDown = async (evt: React.KeyboardEvent<unknown>) => {
        evt.stopPropagation();
        const evtKeyCode = evt.keyCode;

        const scenarioId = this.props.scenarioId;
        const { selectedSuggestionIndex } = getScenarioStore(scenarioId);

        // Dispatch action to modify store based on key down.
        onKeyDownSearchInput(evtKeyCode, this.props.scenarioId);

        switch (evtKeyCode) {
            case KeyboardCharCodes.Enter: {
                // Clear selection (non-default behavior of IE/Edge).
                this.clearSelection();

                onEnterPressedSearchInput(selectedSuggestionIndex, scenarioId);
                break;
            }
            case KeyboardCharCodes.Backspace: {
                const onBackspacePressedSearchInput = await lazyOnBackspacePressedSearchInput.import();
                onBackspacePressedSearchInput(this.getCursorPosition(), scenarioId);
                break;
            }
            case KeyboardCharCodes.Up_arrow:
                // Prevents cursor from going to beginning of input.
                evt.preventDefault();

                const onUpArrowPressedSearchInput = await lazyOnUpArrowPressedSearchInput.import();
                onUpArrowPressedSearchInput(scenarioId);
                break;
            case KeyboardCharCodes.Down_arrow:
                const onDownArrowPressedSearchInput = await lazyOnDownArrowPressedSearchInput.import();
                onDownArrowPressedSearchInput(scenarioId);
                break;
            case KeyboardCharCodes.Left_arrow: {
                const onLeftArrowPressedSearchInput = await lazyOnLeftArrowPressedSearchInput.import();
                onLeftArrowPressedSearchInput(this.getCursorPosition(), scenarioId);
                break;
            }
            case KeyboardCharCodes.Esc: {
                // Prevents IE/Edge behavior of putting last text back into the input.
                evt.preventDefault();

                onEscapePressedSearchInput(scenarioId);
                break;
            }
            default:
                break;
        }
    };

    /**
     * Click handler when user clicks in input.
     */
    private onClick = (evt: React.MouseEvent<unknown>) => {
        evt.stopPropagation();
        setIsSuggestionsDropdownVisible(true, this.props.scenarioId);
    };

    /**
     * Focus handler for when the SearchInput component receives focus.
     */
    private onFocus = () => {
        onSearchInputFocused(this.props.scenarioId);
    };

    /**
     * Need to do this synchronously or there'll be rendering issues and the value
     * of the input element will be empty.
     */
    private onSearchTextChange = () => {
        const scenarioId = this.props.scenarioId;

        const searchText = this.searchInput.value;

        onSearchTextChanged(searchText, scenarioId);
        setSearchTextForSuggestion(searchText, scenarioId);
        getSuggestions(scenarioId);

        // If suggestions dropdown is hidden, show it.
        if (!getScenarioStore(scenarioId).isSuggestionsDropdownVisible) {
            setIsSuggestionsDropdownVisible(true, scenarioId);
        }
    };

    /**
     * Gets cursor position if no text is selected. If text is selected, then
     * -1 is returned.
     */
    private getCursorPosition() {
        /**
         * If reference to input is null (component unmounted), return -1
         * indicating that cursor position shouldn't be used.
         */
        if (!this.searchInput) {
            return -1;
        }

        const isTextSelected = this.searchInput.selectionStart !== this.searchInput.selectionEnd;
        return isTextSelected ? -1 : this.searchInput.selectionStart;
    }

    /**
     * Clears selected text in the input.
     */
    private clearSelection() {
        const searchInputEnd = this.searchInput.value.length;
        this.searchInput.setSelectionRange(searchInputEnd, searchInputEnd);
    }
}
